import { useCallback, useEffect, useState } from 'react';
import { Empty, Tree, Spin, Badge, Input, Tooltip } from 'antd';
import type { TreeDataNode } from 'antd';
import { IconSearch, IconX, IconCornerDownRight } from '@tabler/icons-react';
import { useOrgUnitsTree } from '@/entities/organization/api/hooks';
import { fetchOrgUnitsTree } from '@/entities/organization/api/orgUnitsApi';
import type { OrgUnitTreeItem } from '@/entities/organization/model/organizationSchema';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import s from './OrgTreeDrawer.module.scss';

export type OrgTreeSelectOptions = {
  /** Не закрывать drawer родителя (например после «Показать в дереве»). */
  keepDrawerOpen?: boolean;
};

type Props = {
  onSelect: (orgUnitId: string, orgUnitName: string, options?: OrgTreeSelectOptions) => void;
  selectedOrgUnitId?: string;
};

function highlightMatch(text: string, query: string) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className={s.root__highlight}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

function buildAncestorPath(targetId: string, items: OrgUnitTreeItem[]): string[] {
  const map = new Map(items.map((i) => [i.id, i]));
  const path: string[] = [];
  let currentId: string | null = map.get(targetId)?.parent_id ?? null;
  const seen = new Set<string>();
  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    path.unshift(currentId);
    currentId = map.get(currentId)?.parent_id ?? null;
  }
  return path;
}

function itemToTreeNode(item: OrgUnitTreeItem, childrenMap: Map<string, OrgUnitTreeItem[]>): TreeDataNode {
  const cached = childrenMap.get(item.id);
  return {
    key: item.id,
    title: (
      <span className={s['root__tree-label']}>
        <span className={s['root__tree-name']}>{item.name}</span>
      </span>
    ),
    isLeaf: !item.has_children,
    children: cached ? cached.map((c) => itemToTreeNode(c, childrenMap)) : undefined,
  };
}

export function OrgTreeDrawer({ onSelect, selectedOrgUnitId }: Props) {
  const [searchText, setSearchText] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>(selectedOrgUnitId ? [selectedOrgUnitId] : []);
  const [childrenMap, setChildrenMap] = useState<Map<string, OrgUnitTreeItem[]>>(new Map());
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

  const [revealTarget, setRevealTarget] = useState<{ id: string; name: string; ancestorIds: string[] } | null>(null);

  const isSearching = searchText.trim().length >= 2;
  const { data: rootData, isLoading: rootLoading } = useOrgUnitsTree(isSearching ? { q: searchText.trim() } : {});

  const treeData = (rootData?.items ?? []).map((item) => itemToTreeNode(item, childrenMap));

  const handleLoadData = useCallback(
    async (node: TreeDataNode) => {
      const nodeId = node.key as string;
      if (childrenMap.has(nodeId)) return;

      setLoadingKeys((prev) => new Set(prev).add(nodeId));
      try {
        const resp = await fetchOrgUnitsTree({ root_id: nodeId });
        setChildrenMap((prev) => {
          const next = new Map(prev);
          next.set(nodeId, resp.items);
          return next;
        });
      } finally {
        setLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    [childrenMap],
  );

  const handleTreeSelect = useCallback(
    (keys: React.Key[]) => {
      setSelectedKeys(keys);
      if (keys.length === 0) return;
      const nodeId = keys[0] as string;
      const item =
        (rootData?.items ?? []).find((i) => i.id === nodeId) ??
        [...childrenMap.values()].flat().find((i) => i.id === nodeId);
      if (item) {
        onSelect(item.id, item.name);
      }
    },
    [rootData, childrenMap, onSelect],
  );

  const handleSearchItemClick = useCallback(
    (item: OrgUnitTreeItem) => {
      setSelectedKeys([item.id]);
      onSelect(item.id, item.name);
    },
    [onSelect],
  );

  const handleRevealInTree = useCallback(
    (item: OrgUnitTreeItem) => {
      const searchItems = rootData?.items ?? [];
      const ancestorIds = buildAncestorPath(item.id, searchItems);
      setRevealTarget({ id: item.id, name: item.name, ancestorIds });
      setSearchText('');
    },
    [rootData],
  );

  useEffect(() => {
    if (!revealTarget || isSearching) return;
    let cancelled = false;

    (async () => {
      const { id, name, ancestorIds } = revealTarget;

      for (const ancestorId of ancestorIds) {
        if (cancelled) return;
        try {
          const resp = await fetchOrgUnitsTree({ root_id: ancestorId });
          setChildrenMap((prev) => {
            const next = new Map(prev);
            next.set(ancestorId, resp.items);
            return next;
          });
        } catch {
          break;
        }
      }

      if (cancelled) return;

      setExpandedKeys(ancestorIds);
      setSelectedKeys([id]);
      onSelect(id, name, { keepDrawerOpen: true });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.querySelector('.ant-tree-treenode-selected');
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });

      setRevealTarget(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [revealTarget, isSearching, onSelect]);

  if (rootLoading && !rootData) return <PageSpinner />;

  if (treeData.length === 0 && !isSearching) return <Empty description="Оргструктура пуста" />;

  const searchResults = rootData?.items ?? [];
  const matchCount = isSearching ? searchResults.length : 0;

  return (
    <div className={s.root}>
      <div className={s.root__searchWrap}>
        <Input
          placeholder="Поиск подразделения..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<IconSearch size={14} className={s.root__searchIcon} />}
          suffix={
            searchText ? (
              <span className={s.root__searchActions}>
                {isSearching ? <Badge count={matchCount} size="small" className={s.root__searchBadge} /> : null}
                <IconX size={14} className={s.root__clearIcon} onClick={() => setSearchText('')} />
              </span>
            ) : null
          }
          className={s.root__search}
          allowClear={false}
        />
      </div>

      {isSearching ? (
        <div className={s.root__flatList}>
          {searchResults.length > 0 ? (
            searchResults.map((item) => (
              <div
                key={item.id}
                className={`${s.root__flatItem} ${selectedKeys.includes(item.id) ? s.root__flatItem_selected : ''}`}
                onClick={() => handleSearchItemClick(item)}
              >
                <div className={s.root__flatContent}>
                  <div className={s.root__flatName}>{highlightMatch(item.name, searchText.trim())}</div>
                  {item.breadcrumb_path ? <div className={s.root__flatPath}>{item.breadcrumb_path}</div> : null}
                </div>
                <Tooltip title="Показать в дереве" mouseEnterDelay={0.4}>
                  <span
                    className={s.root__flatReveal}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRevealInTree(item);
                    }}
                  >
                    <IconCornerDownRight size={14} />
                  </span>
                </Tooltip>
              </div>
            ))
          ) : (
            <Empty description="Ничего не найдено" />
          )}
        </div>
      ) : (
        <Spin spinning={loadingKeys.size > 0} className={s.root__spin}>
          {treeData.length > 0 ? (
            <Tree
              className={s.root__tree}
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              selectedKeys={selectedKeys}
              onSelect={handleTreeSelect}
              loadData={handleLoadData}
              blockNode
            />
          ) : (
            <Empty description="Ничего не найдено" />
          )}
        </Spin>
      )}
    </div>
  );
}
